<script setup lang="ts">
  import { ref, onMounted, computed } from 'vue'
  import { useRoute } from 'vue-router'
  import { Clock, ArrowUpRight, Award, FileText } from 'lucide-vue-next'
  import { Button } from '@/components/ui/button'
  import { Card, CardContent } from '@/components/ui/card'
  import SelectField from '@/components/admin/form/SelectField.vue'
  import InputField from '@/components/admin/form/InputField.vue'
  import AudioPlaybackButton from '@/components/app/calls/audioPlaybackButton.vue'
  import CallRating from '@/components/app/calls/callRatingModal.vue'
  import {
    CallsService,
    ClassificationEnum,
    OrganisationsService,
    type CustomerCall as Call,
    type ClientData,
    type CustomerCallResult,
    type CustomerOrganisation,
    type LawyerFeedback
  } from '@/types/generated'
  import VueMarkdown from 'vue-markdown-render'
  import { useTranslation } from '@/composables/useTranslation'
  import router from '@/router'
  import TrashIcon from '@/components/icons/TrashIcon.vue'
  import { ROUTES } from '@/router/routes'
  import SpaCompatComponent from '@/components/app/SpaCompatComponent.vue'
  import AutoSavingForm from '@/components/admin/form/AutoSavingForm.vue'
  import type { GenericObject } from 'vee-validate'
  import * as yup from 'yup'

  const route = useRoute()
  const params = route.params as { uuid: string }
  const isVoiceStandAlone = ref(false)
  const call = ref<Call | null>(null)
  const lawyerFeedback = ref<LawyerFeedback | null>(null)
  const isRatingModalOpen = ref(false)
  const isCreatingCase = ref(false)

  const titleOptions = [
    { value: '', title: 'Nicht ausgewählt' },
    { value: 'Herr', title: 'Herr' },
    { value: 'Frau', title: 'Frau' }
  ]

  const defaultClientData: ClientData = {
    first_name: '',
    last_name: '',
    title: '',
    email: '',
    phone: ''
  }

  const defaultCallResult: CustomerCallResult = {
    uuid: '',
    classification: '' as ClassificationEnum,
    data: {
      client: defaultClientData,
      law: {
        field: '',
        topic: ''
      },
      opponent: {}
    },
    inquiry: '',
    first_name_recording_start_seconds: null,
    first_name_recording_end_seconds: null,
    last_name_recording_start_seconds: null,
    last_name_recording_end_seconds: null,
    email_recording_start_seconds: null,
    email_recording_end_seconds: null,
    phone_number_recording_start_seconds: null,
    phone_number_recording_end_seconds: null
  }

  onMounted(async () => {})

  const fetchCall = async () => {
    const fetchedCall = (await CallsService.callsRetrieve({ uuid: params.uuid })) as Call
    call.value = {
      ...fetchedCall,
      result: {
        ...defaultCallResult,
        ...fetchedCall.result
      }
    }
    lawyerFeedback.value = fetchedCall.lawyer_feedback
  }
  const fetchCurrentOrg = async () => {
    const response = (await OrganisationsService.organisationsRetrieve({
      uuid: 'current'
    })) as CustomerOrganisation

    isVoiceStandAlone.value = response.enable_cases || false
  }

  onMounted(() => {
    Promise.all([fetchCurrentOrg(), fetchCall()])
  })

  // Audio recording availability checks
  const canShowFirstNameRecording = computed(() => {
    return (
      call.value?.phone_number?.can_record_calls &&
      call.value?.result?.first_name_recording_start_seconds !== null &&
      call.value?.result?.first_name_recording_end_seconds !== null
    )
  })

  const canShowLastNameRecording = computed(() => {
    return (
      call.value?.phone_number?.can_record_calls &&
      call.value?.result?.last_name_recording_start_seconds !== null &&
      call.value?.result?.last_name_recording_end_seconds !== null
    )
  })

  const canShowPhoneNumberRecording = computed(() => {
    return (
      call.value?.phone_number?.can_record_calls &&
      call.value?.result?.phone_number_recording_start_seconds !== null &&
      call.value?.result?.phone_number_recording_end_seconds !== null
    )
  })

  const canShowEmailRecording = computed(() => {
    return (
      call.value?.phone_number?.can_record_calls &&
      call.value?.result?.email_recording_start_seconds !== null &&
      call.value?.result?.email_recording_end_seconds !== null
    )
  })

  const createCase = async () => {
    if (!call.value) return

    isCreatingCase.value = true
    try {
      const response = await CallsService.callsCreateCaseCreate({ uuid: call.value.uuid })
      window.location.href = `/cases/${response.case_uuid}`
    } catch (error) {
      console.error('Error creating case:', error)
      isCreatingCase.value = false
    }
  }

  const callToActionButton = computed(() => {
    if (!call.value) return null

    if (call.value.case_uuid) {
      return {
        text: 'Zur Anfrage',
        action: () => (window.location.href = `/cases/${call.value!.case_uuid}`),
        disabled: false
      }
    }

    return {
      text: 'In Anfrage konvertieren',
      action: createCase,
      disabled: isCreatingCase.value
    }
  })

  const handleRatingClick = () => {
    isRatingModalOpen.value = true
  }

  const handleRatingUpdated = (newRating: string) => {
    lawyerFeedback.value = {
      rating: newRating,
      uuid: lawyerFeedback.value?.uuid || ''
    }
    isRatingModalOpen.value = false
  }

  const deleteCall = async () => {
    await CallsService.callsDestroy({ uuid: call.value.uuid })
    await router.push(ROUTES.APP.CALLS.ROOT)
  }

  const { t } = useTranslation({
    en: {
      CallFrom: 'Call from',
      ExistingClient: 'Existing Client',
      NoFirstName: 'No First Name',
      NoLastName: 'No Last Name',
      RecievedOn: 'Recieved on',
      Title: 'Title',
      ContactDetails: 'Contact Details',
      FirstName: 'First Name',
      LastName: 'Last Name',
      Phone: 'Phone',
      NoCaseInformation: 'No case information',
      additionalInformation: 'Additional Information',
      emailAddress: 'Email Address',
      NoadditionalInformation: 'No additional information provided',
      NoEmail: 'No email provided',
      PhoneNumber: 'Phone Number',
      NoPhone: 'No phone number provided',
      Name: 'Name',
      CallDetails: 'Call Details',
      CallContent: 'Call Content'
    },
    de: {
      CallFrom: 'Anruf von',
      ExistingClient: 'Bestandsmandant',
      NoFirstName: 'Kein Vorname',
      NoLastName: 'Kein Nachname',
      RecievedOn: 'Eingegangen am',
      Title: 'Anrede',
      ContactDetails: 'Kontaktdaten',
      FirstName: 'Vorname',
      LastName: 'Nachname',
      Phone: 'Telefon',
      NoCaseInformation: 'Keine Fall Informationen',
      additionalInformation: 'Zusätzliche Informationen',
      emailAddress: 'E-Mail-Adresse',
      NoadditionalInformation: 'Keine zusätzlichen Informationen angegeben',
      NoEmail: 'Keine E-Mail angegeben',
      PhoneNumber: 'Telefonnummer',
      NoPhone: 'Keine Telefonnummer angegeben',
      Name: 'Name',
      CallDetails: 'Fall Details',
      CallContent: 'Inhalt des Anrufes'
    }
  })

  const handleSave = async (values: GenericObject) => {
    console.log('Saving values:', values)
    await CallsService.callsPartialUpdate({
      uuid: call.value!.uuid!,
      requestBody: { result: { data: { client: values } } }
    })
  }
</script>

<template>
  <SpaCompatComponent>
    <div
      v-if="call"
      class="min-h-screen"
    >
      <div class="mx-auto space-y-4">
        <!-- Header -->
        <div class="pt-6">
          <div class="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div class="space-y-1">
              <div class="flex flex-wrap items-center gap-2 sm:gap-3">
                <h1 class="text-xl font-semibold text-gray-900 sm:text-2xl">
                  {{ t('CallFrom') }} {{ call.result.data.client.first_name || t('NoFirstName') }}
                  {{ call.result.data.client.last_name || t('NoLastName') }}
                </h1>
              </div>
              <div class="flex items-center gap-2 text-sm text-gray-500">
                <Clock class="h-4 w-4" />
                Eingegangen am {{ new Date(call.call_start_time).toLocaleString('de-DE') }}
              </div>
            </div>
            <div class="flex flex-wrap items-center gap-3">
              <Button
                class="inline-flex items-center gap-x-2 rounded-lg border border-gray-200 bg-white text-sm font-medium
                  text-purple-500 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
                  disabled:pointer-events-none disabled:opacity-50"
                :disabled="lawyerFeedback !== null"
                @click="handleRatingClick"
              >
                <Award class="h-4 w-4" />
                {{
                  lawyerFeedback
                    ? `Ihre Bewertung: ${parseInt(lawyerFeedback.rating)} ${
                        lawyerFeedback.rating < '1.1' ? 'Stern' : 'Sterne'
                      }`
                    : 'Marie Bewerten'
                }}
              </Button>
              <Button
                class="inline-flex items-center gap-x-2 rounded-lg border border-gray-200 bg-white text-sm font-medium
                  text-red-500 shadow-sm hover:bg-gray-50 focus:bg-gray-50 focus:outline-none
                  disabled:pointer-events-none disabled:opacity-50"
                @click="deleteCall"
              >
                <TrashIcon class="h-4 w-4" />
                Löschen
              </Button>
              <Button
                v-if="isVoiceStandAlone"
                class="flex-1 gap-2 bg-purple-600 text-white hover:bg-purple-700 sm:flex-none"
                :disabled="callToActionButton?.disabled"
                @click="callToActionButton?.action"
              >
                <ArrowUpRight class="h-4 w-4" />
                {{ callToActionButton?.text }}
              </Button>
            </div>
          </div>
        </div>

        <div class="grid gap-4 pt-4 sm:gap-6 lg:grid-cols-3">
          <!-- Contact Information -->
          <Card class="bg-white lg:col-span-1">
            <CardContent class="p-4 sm:p-6">
              <div class="flex items-center justify-between">
                <h2 class="text-lg font-medium text-gray-900">{{ t('ContactDetails') }}</h2>
                <Button
                  variant="ghost"
                  size="sm"
                  class="text-gray-500 hover:text-gray-700"
                >
                  <FileText class="h-4 w-4" />
                </Button>
              </div>
              <AutoSavingForm @save="handleSave">
                <div class="mt-6 space-y-5">
                  <div class="space-y-2">
                    <label class="text-sm font-medium text-gray-700">{{ t('Title') }}</label>
                    <SelectField
                      v-model="call.result.data.client.title"
                      :options="titleOptions"
                      placeholder="Nicht ausgewählt"
                      name="title"
                      :rules="yup.string().notRequired()"
                    />
                  </div>

                  <div class="space-y-1">
                    <div class="flex items-end gap-2">
                      <div class="min-w-0 flex-1">
                        <InputField
                          v-model="call.result.data.client.first_name"
                          name="first_name"
                          :label="t('FirstName')"
                          :rules="yup.string().notRequired()"
                        />
                      </div>
                      <AudioPlaybackButton
                        v-if="canShowFirstNameRecording"
                        :audioUrl="call.audio_recording_url || ''"
                        :endTime="call.result.first_name_recording_end_seconds || ''"
                        :startTime="call.result.first_name_recording_start_seconds || ''"
                      />
                    </div>
                  </div>

                  <div class="space-y-1">
                    <div class="flex items-end gap-2">
                      <div class="min-w-0 flex-1">
                        <InputField
                          v-model="call.result.data.client.last_name"
                          :label="t('LastName')"
                          name="last_name"
                          :rules="yup.string().notRequired()"
                        />
                      </div>

                      <AudioPlaybackButton
                        v-if="canShowLastNameRecording"
                        :audioUrl="call.audio_recording_url"
                        :endTime="call.result.last_name_recording_end_seconds"
                        :startTime="call.result.last_name_recording_start_seconds"
                      />
                    </div>
                  </div>

                  <div class="space-y-1">
                    <div class="flex items-end gap-2">
                      <div class="min-w-0 flex-1">
                        <InputField
                          v-model="call.result.data.client.email"
                          type="email"
                          name="email"
                          label="E-Mail"
                          :rules="yup.string().email().notRequired()"
                        />
                      </div>

                      <AudioPlaybackButton
                        v-if="canShowEmailRecording"
                        :audioUrl="call.audio_recording_url"
                        :endTime="call.result.email_recording_end_seconds"
                        :startTime="call.result.email_recording_start_seconds"
                      />
                    </div>
                  </div>

                  <div class="space-y-1">
                    <div class="flex items-end gap-2">
                      <div class="min-w-0 flex-1">
                        <InputField
                          v-model="call.result.data.client.phone"
                          type="tel"
                          name="phone"
                          :label="t('Phone')"
                          :rules="yup.string().notRequired()"
                        />
                      </div>

                      <AudioPlaybackButton
                        v-if="canShowPhoneNumberRecording"
                        :audioUrl="call.audio_recording_url"
                        :endTime="call.result.phone_number_recording_end_seconds"
                        :startTime="call.result.phone_number_recording_start_seconds"
                        class="flex h-10 w-10 items-center justify-center rounded bg-purple-50 text-purple-600"
                      />
                    </div>
                  </div>
                </div>
              </AutoSavingForm>
            </CardContent>
          </Card>

          <!-- Call Content -->
          <Card class="bg-white lg:col-span-2">
            <CardContent class="p-4 sm:p-6">
              <h2 class="text-lg font-medium text-gray-900">{{ t('CallContent') }}</h2>

              <div class="mt-6 space-y-6 sm:space-y-8">
                <div class="">
                  <VueMarkdown
                    v-if="call.result.inquiry != ''"
                    :source="call.result.inquiry"
                  />
                  <div v-else>{{ t('NoCaseInformation') }}</div>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>

      <!-- Rating Modal -->
      <CallRating
        v-if="call"
        :call-uuid="call.uuid"
        :is-open="isRatingModalOpen"
        @close="() => (isRatingModalOpen = false)"
        @rating-updated="handleRatingUpdated"
      />
    </div>
  </SpaCompatComponent>
</template>
